package ui.app.rss.instance.detail


import androidx.compose.runtime.*
import androidx.compose.ui.graphics.vector.*
import common.frontend.api.*
import provider.*
import tk.mallumo.compose.navigation.*
import tk.mallumo.compose.navigation.viewmodel.*
import tools.*
import ui.app.rss.instance.detail.AppRssDetailInstanceVM.Action
import ui.widget.*

@Stable
data class AppRssDetailInstanceScope(
    val nav: Navigation,
    val vm: AppRssDetailInstanceVM,
    val args: AppRssDetailInstanceArgs,
    val lang: String,
    val action: (Action) -> Unit,
    val user: Io.User,
    val display: CommonDisplayProvider
) {

    companion object {
        @Composable
        fun remember(): AppRssDetailInstanceScope {
            val nav = LocalNavigation.current
            val arg = LocalNavigationArgs.current
            val vm = nav.viewModel(AppRssDetailInstanceVM::class)
            val localization = LocalLocalization.current
            val action = Action.rememberAction(vm)
            val account = LocalAccountProvider.current
            val display = LocalDisplayProvider.current
            return remember {
                AppRssDetailInstanceScope(
                    nav = nav,
                    vm = vm,
                    args = arg.args(),
                    lang = localization.language.short.first(),
                    action = action,
                    user = account.current!!,
                    display = display
                ).also {
                    vm.init(it)
                }
            }
        }
    }

    class Wrapper(val data: Io.RssInstance) {

        private val _changed = mutableStateOf(false)
        val changed: State<Boolean> get() = _changed


        data class DropDownEntry(
            val channel: Io.RssInstance.Channel,
            override val label: String = channel.name,
            override val image: ImageVector? = null
        ) : DropDownItem

        val channels = data.channels
            .sortedByDescending { it.id }
            .map { DropDownEntry(it) }

        val selectedChannel = mutableStateOf(
            channels.firstOrNull { it.channel.id == data.item.channelId }
                ?: channels.first()
        )

        val name = EFieldTextString(
            property = data.item::name,
            condition = { it.trim().takeIf { it.isNotEmpty() } },
            onChange = {
                makeChange()
            }
        )
        val description = EFieldTextString(
            property = data.item::description,
            condition = { it.trim().takeIf { it.isNotEmpty() } },
            onChange = {
                makeChange()
            }
        )

        val logo = EFieldRegularLong(
            property = data.item::logo,
            onChange = {
                makeChange()
            }
        )
        val channelId = EFieldRegularLong(
            property = data.item::channelId,
            onChange = {
                makeChange()
            }
        )

        val showListingImage = EFieldRegularBoolean(
            property = data.item::showListingImage,
            onChange = {
                makeChange()
            }
        )

        val showListingTitle = EFieldRegularBoolean(
            property = data.item::showListingTitle,
            onChange = {
                makeChange()
            }
        )

        val showListingDescription = EFieldRegularBoolean(
            property = data.item::showListingDescription,
            onChange = {
                makeChange()
                if (!it) showListingDescriptionAsHtml.set(false)
            }
        )

        val showDetail = EFieldRegularBoolean(
            property = data.item::showDetail,
            onChange = {
                makeChange()
                if (!it) showItemDescriptionAsHtml.set(false)
            }
        )

        val showItemDescriptionAsHtml = EFieldRegularBoolean(
            property = data.item::showItemDescriptionAsHtml,
            onChange = {
                makeChange()
            }
        )

        val showListingDescriptionAsHtml = EFieldRegularBoolean(
            property = data.item::showListingDescriptionAsHtml,
            onChange = {
                makeChange()
            }
        )

        val labelSearch = EFieldTextString(
            property = data.item::labelSearch,
            condition = { it.trim().takeIf { it.isNotEmpty() } },
            onChange = {
                makeChange()
            }
        )

        val labelCategory = EFieldTextString(
            property = data.item::labelCategory,
            condition = { it.trim().takeIf { it.isNotEmpty() } },
            onChange = {
                makeChange()
            }
        )

        val labelItemDescription = EFieldTextString(
            property = data.item::labelItemDescription,
            condition = { it.trim().takeIf { it.isNotEmpty() } },
            onChange = {
                makeChange()
            }
        )

        val labelItemCategory = EFieldTextString(
            property = data.item::labelItemCategory,
            condition = { it.trim().takeIf { it.isNotEmpty() } },
            onChange = {
                makeChange()
            }
        )

        val labelItemAuthor = EFieldTextString(
            property = data.item::labelItemAuthor,
            condition = { it.trim().takeIf { it.isNotEmpty() } },
            onChange = {
                makeChange()
            }
        )

        val labelItemDetails = EFieldTextString(
            property = data.item::labelItemDetails,
            condition = { it.trim().takeIf { it.isNotEmpty() } },
            onChange = {
                makeChange()
            }
        )

        val labelItemPublished = EFieldTextString(
            property = data.item::labelItemPublished,
            condition = { it.trim().takeIf { it.isNotEmpty() } },
            onChange = {
                makeChange()
            }
        )
        val labelItemAttachments = EFieldTextString(
            property = data.item::labelItemAttachments,
            condition = { it.trim().takeIf { it.isNotEmpty() } },
            onChange = {
                makeChange()
            }
        )
        val labelItemValidUntil = EFieldTextString(
            property = data.item::labelItemValidUntil,
            condition = { it.trim().takeIf { it.isNotEmpty() } },
            onChange = {
                makeChange()
            }
        )

        private fun makeChange() {
            _changed.value = allFields().any { it.change }

        }

        fun confirmChanges() {
            allFields().forEach {
                it.applyChanges()
            }
            makeChange()
        }

        private fun allFields() = listOf(
            labelSearch,
            labelCategory,
            labelItemDescription,
            labelItemCategory,
            labelItemAuthor,
            labelItemDetails,
            labelItemPublished,
            labelItemValidUntil,
            labelItemAttachments,

            name,
            description,
            logo,
            channelId,

            showDetail,
            showListingImage,
            showListingDescription,

            showListingTitle,
            showListingDescriptionAsHtml,
            showItemDescriptionAsHtml
        )
    }
}
